import React, { useCallback, useEffect, useMemo, useState } from "react";
import MenuCategoryCard from "src/features/MenuCategories/components/MenuCategoryCard";
import { useDispatch, useSelector } from "react-redux";
import { delete_menu_category_modal } from "src/store/features/category-slice";
import SearchBar from "src/components/common/SearchBar";
import MenuCategoryModal from "src/features/MenuCategories/components/MenuCategoryModal";
import { MenuCategory } from "src/interfaces/menu.interface";
import { RootState } from "src/store/index";
import Spinner from "src/components/common/Spinner";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { GET_MENU_CATEGORIES } from "src/constants/queries";
import { MENU_LIST_FILTER_TABS, type MenuListActiveFilter } from "src/constants/menuListActiveFilter";

const MenuCategoriesPage = () => {
  const [showCreateMenuCategoryModal, setShowCreateMenuCategoryModal] = useState(false);
  const [editMenuCategory, setEditMenuCategory] = useState<MenuCategory | null>(null);
  const dispatch = useDispatch();
  const { hasRestaurant } = useAuth();
  const { axBe } = useAxiosInterceptor();

  const [activeFilter, setActiveFilter] = useState<MenuListActiveFilter>("ACTIVE");
  const [serverCategories, setServerCategories] = useState<MenuCategory[]>([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const categoriesRevision = useSelector((state: RootState) => (state.category as any).categoriesRevision ?? 0);

  const sortCategoriesByName = useCallback((categories: MenuCategory[]) => {
    return categories.slice().sort((previousCategory, nextCategory) => {
      const previousName = (previousCategory.name || "").trim().toLowerCase();
      const nextName = (nextCategory.name || "").trim().toLowerCase();
      const nameCompare = previousName.localeCompare(nextName);

      if (nameCompare !== 0) return nameCompare;

      return (previousCategory.correlationId || "").localeCompare(
        nextCategory.correlationId || ""
      );
    });
  }, []);

  const fetchPageCategories = useCallback(async () => {
    if (!hasRestaurant?.correlationId) return;
    setPageLoading(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: GET_MENU_CATEGORIES,
        variables: {
          storeCorrelationId: hasRestaurant.correlationId,
          activeFilter,
        },
      });
      const raw: MenuCategory[] = data?.data?.categoriesByStoreCorrelationId ?? [];
      setServerCategories(sortCategoriesByName(raw));
    } catch (e) {
      console.error(e);
      setServerCategories([]);
    } finally {
      setPageLoading(false);
    }
  }, [hasRestaurant?.correlationId, axBe, activeFilter, sortCategoriesByName]);

  useEffect(() => {
    fetchPageCategories();
  }, [fetchPageCategories, categoriesRevision]);

  const displayedCategories = useMemo(() => {
    const t = searchQuery.trim().toLowerCase();
    if (!t) return serverCategories;
    return sortCategoriesByName(
      serverCategories.filter((c) => (c.name ?? "").toLowerCase().includes(t))
    );
  }, [serverCategories, searchQuery, sortCategoriesByName]);

  const handleDeleteCategory = (categoryId: string) => {
    dispatch(delete_menu_category_modal(categoryId));
  };

  return (
    <div className="flex w-full min-w-0 shrink-0 flex-col pb-4">
      <h1 className="text-[20px] font-semibold text-[#111] mb-1" style={{ fontWeight: 600 }}>
        Categories
      </h1>
      <p className="text-xs text-[#6B7280] mb-4">
        Open a category to view and manage its items.
      </p>

      <div className="flex w-full min-w-0 shrink-0 flex-col gap-3 rounded-lg border border-[#E5E7EB] bg-white px-3 py-2 mb-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="w-full min-w-0 sm:flex-1 sm:max-w-[260px]">
          <SearchBar placeholder="Search Category..." value={searchQuery} onChange={setSearchQuery} />
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:justify-end sm:shrink-0">
          <div
            className="flex flex-wrap items-center gap-1 rounded-lg border border-[#F1F1F2] p-0.5 bg-white w-full sm:w-fit"
            role="group"
            aria-label="Filter categories by status"
          >
            {MENU_LIST_FILTER_TABS.map(({ id, label }) => {
              const selected = activeFilter === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveFilter(id)}
                  className={`h-[38px] px-3 text-xs font-medium rounded-md transition-all outline-none focus-visible:ring-2 focus-visible:ring-[#FD7657]/40 flex-1 sm:flex-none min-w-0 ${
                    selected
                      ? "bg-[#FD7657] text-white shadow-sm"
                      : "bg-white text-[#6b7280] hover:bg-[#FEF5F4] hover:text-[#FD7657]"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="h-8 px-4 rounded-lg text-sm font-medium text-white bg-[#FD7657] hover:bg-[#e45e41] shrink-0 transition-colors w-full sm:w-auto"
            onClick={() => setShowCreateMenuCategoryModal(true)}
          >
            Add Category
          </button>
        </div>
      </div>

      <div className="w-full min-w-0">
        {pageLoading ? (
          <div className="flex justify-center w-full py-12">
            <Spinner />
          </div>
        ) : displayedCategories.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-y-4 gap-x-6">
            {displayedCategories.map((menuCategory: MenuCategory, index: number) => (
              <MenuCategoryCard
                key={menuCategory.correlationId ?? index}
                menuCategory={menuCategory}
                restaurantCorrelationId={hasRestaurant?.correlationId ?? ""}
                restaurantName={hasRestaurant?.name}
                onEdit={setEditMenuCategory}
                onDelete={handleDeleteCategory}
              />
            ))}
          </div>
        ) : (
          <p className="text-[#6B7280] py-12 text-center text-sm">
            No categories yet. Click &quot;Add Category&quot; to create one.
          </p>
        )}
      </div>
      {(showCreateMenuCategoryModal || !!editMenuCategory) && (
        <MenuCategoryModal
          menuCategoryTitle={!!editMenuCategory ? "Edit Category" : "Create Category"}
          setShowCreateModal={() =>
            !!editMenuCategory
              ? setEditMenuCategory(null)
              : setShowCreateMenuCategoryModal(!showCreateMenuCategoryModal)
          }
          editMenuCategory={editMenuCategory}
        />
      )}
    </div>
  );
};

export default MenuCategoriesPage;
